import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as React from "react";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbSize, AbSizeContext } from "abreact/misc/AbSize";
import * as AbHooks from "abreact/utils/AbHooks";
import { AbPopupButton } from "abreact/misc/AbPopupButton";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { Orientation } from "abreact/incubator/ui/SharedTypes";
import { focusable } from "tabbable";
import { AB_COLLAPSE_OVERFLOW_BUTTON, AbCollapseBar } from "./AbCollapseBar";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { AB_ITEM_SEPARATOR } from "abweb/collection/AbItemSeparator";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AB_ITEM_SPACER } from "abweb/collection/AbItemSpacer";
import { useNoTabStops } from "abreact/utils/AbHooks";
import { LogUtils } from "abweb/utils/log/LogUtils";
const LOGGER = LogUtils.getLogger(__filename);
export const AbToolbar = props => {
  const groups = filterToolbarGroups(props.groups);
  const fixedGroup = makeGroupSafe(props.fixedGroup);
  const rootRef = React.useRef(null);
  const popupElementRef = React.useRef(null);
  const positioner = AbHooks.useLazyRef(() => PopupUtils.createToolbarPositioner({
    toolbarRef: rootRef
  }));
  const size = props.size ?? AbSize.MEDIUM;
  return /*#__PURE__*/React.createElement(AbSizeContext.Provider, {
    value: size === AbSize.MEDIUM ? AbSize.SMALL : AbSize.EXTRA_SMALL
  }, /*#__PURE__*/React.createElement(AbToolbarContext.Provider, {
    value: true
  }, /*#__PURE__*/React.createElement(AbCollapseBar, _extends({
    keyFunction: group => ArrayUtils.safeJoin(groupToPrimitives(group)),
    abRef: rootRef,
    isToolbar: true,
    items: [...ArrayUtils.intersperse(groups.map(item => AbEnriched.create(item, {
      className: "ab-toolbar-group"
    })), () => AB_ITEM_SEPARATOR), AB_ITEM_SEPARATOR, AB_COLLAPSE_OVERFLOW_BUTTON, AB_ITEM_SPACER, fixedGroup && AbEnriched.create(fixedGroup, {
      priority: 100,
      // Higher priority than other groups, but still collapsible
      className: "ab-fixed-toolbar-group"
    })],
    itemRenderer: item => /*#__PURE__*/React.createElement(AbToolbarGroup, {
      group: item,
      inPopup: false
    }),
    renderCollapsedButton: items => {
      return /*#__PURE__*/React.createElement(AbPopupButton, {
        icon: "absdk:double-chevron-right",
        key: "toolbar-popup-button",
        dataTestId: "toolbar-expando-button",
        className: "ab-toolbar-popup-button",
        popupProps: {
          className: "ab-toolbar-popup",
          positioner: positioner.current,
          resizeBoxRef: popupElementRef,
          setFocus: () => {
            const popupElement = popupElementRef.current?.element;
            if (!popupElement) return;
            const focusableElements = focusable(popupElement);
            focusableElements.find(element => !element.classList.contains("ab-popup-focus-trap"))?.focus();
          }
        },
        popupContents: /*#__PURE__*/React.createElement(AbToolbarPopup, {
          renderGroup: group => /*#__PURE__*/React.createElement(AbToolbarGroup, {
            group: group,
            inPopup: true
          }),
          toolbarGroups: items.filter(item => item !== AB_ITEM_SEPARATOR && item !== AB_ITEM_SPACER),
          size: size
        })
      });
    }
  }, ReactUtils.extractCommonProps(props), {
    className: CssUtils.composeClassNames(props.className, "ab-toolbar", size)
  }))));
};
export const AbToolbarContext = /*#__PURE__*/React.createContext(false);
AbToolbarContext.displayName = "AbToolbarContext";
const AbToolbarGroup = props => {
  const contents = /*#__PURE__*/React.createElement(React.Fragment, null, props.group.items.map(item => {
    const key = TypeUtils.isPrimitive(item) ? TypeUtils.primitiveToKeyString(item) : item.key;
    if (props.inPopup) return /*#__PURE__*/React.createElement("div", {
      className: "ab-popup-toolbar-group-item",
      key: key
    }, item);
    if (typeof item === "string") return /*#__PURE__*/React.createElement("span", {
      key: key
    }, item);
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: key
    }, item);
  }));
  if (props.inPopup) {
    return /*#__PURE__*/React.createElement("div", {
      className: "ab-toolbar-group"
    }, contents);
  }
  return contents;
};
const AbToolbarPopup = props => {
  const rootRef = useNoTabStops();
  return /*#__PURE__*/React.createElement("div", {
    className: ReactUtils.classNamesString(["ab-toolbar", "ab-toolbar-popup-toolbar", "ab-toolbar-vertical"]),
    ref: rootRef,
    role: "toolbar",
    onKeyDown: e => KeyboardUtilsReact.arrowGroupKeyDown(e, Orientation.HORIZONTAL)
  }, /*#__PURE__*/React.createElement(AbSizeContext.Provider, {
    value: props.size === AbSize.MEDIUM ? AbSize.SMALL : AbSize.EXTRA_SMALL
  }, props.toolbarGroups.map((group, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: index
  }, props.renderGroup(group)))));
};
const makeGroupSafe = group => {
  if (!group) return undefined;
  const newItems = TypeUtils.filterOutNullishOrFalse(group.items);
  if (newItems.length === 0) return undefined;
  return {
    items: newItems
  };
};
const groupToPrimitives = group => {
  if (!group) return [];
  return group.items.map(thingToPrimitive);
};
const thingToPrimitive = item => {
  if (typeof item !== "string" && !item.key) LOGGER.warn("All react elements in the toolbar must have a key");
  return typeof item === "string" ? `s-${item}` : `e-${item.key}`;
};
const filterToolbarGroups = groups => {
  return groups.map(makeGroupSafe).filter(group => group !== undefined);
};
//# sourceMappingURL=AbToolbar.js.map
